import React, { useState } from 'react';

const PhotosTab = ({
  photoBoothPhotos,
  bannedPhotos,
  loadingPhotos,
  loadPhotoBoothPhotos,
  loadBannedPhotos,
  handleBanPhoto,
  handleUnbanPhoto,
  handleDeletePhotoPermanently,
}) => {
  const [deleteModal, setDeleteModal] = useState({ show: false, photo: null });
  const backendUrl = (process.env.REACT_APP_API_URL || '').replace('/api', '') || 'http://localhost:3001';

  // Obtener la URL de la imagen o thumbnail para mostrar
  const getPreviewUrl = (photo) => {
    if (photo.mediaType === 'video' && photo.thumbnailUrl) {
      return `${backendUrl}${photo.thumbnailUrl}`;
    }
    return `${backendUrl}${photo.url}`;
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Fotos/Videos del Panel Fotográfico</h2>
        <button
          onClick={() => {
            loadPhotoBoothPhotos();
            loadBannedPhotos();
          }}
          disabled={loadingPhotos}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition disabled:opacity-50 flex items-center"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Actualizar
        </button>
      </div>
      <p className="text-gray-600 mb-6">
        Gestiona las fotos y videos subidos por los asistentes en el panel fotográfico.
      </p>

      {loadingPhotos ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <>
          {/* Fotos/Videos activos */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
              Activos ({photoBoothPhotos.length})
            </h3>
            
            {photoBoothPhotos.length === 0 ? (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                <div className="text-4xl mb-2">📷</div>
                <p>No hay fotos ni videos activos</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {photoBoothPhotos.map((photo) => (
                  <div key={photo.fileName} className="relative group bg-gray-100 rounded-lg overflow-hidden">
                    <img
                      src={getPreviewUrl(photo)}
                      alt={photo.fileName}
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        // Fallback para videos sin thumbnail
                        if (photo.mediaType === 'video') {
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23000" width="100" height="100"/><polygon fill="%23fff" points="30,20 80,50 30,80"/></svg>';
                        }
                      }}
                    />
                    {/* Indicador de video */}
                    {photo.mediaType === 'video' && (
                      <div className="absolute top-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded flex items-center">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                        </svg>
                        Video
                        {photo.duration && ` ${Math.round(photo.duration)}s`}
                      </div>
                    )}
                    <div className="p-3 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                      {photo.uploaderName && (
                        <p className="text-sm font-semibold text-gray-800 truncate">{photo.uploaderName}</p>
                      )}
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleBanPhoto(photo.fileName)}
                          className="px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded text-xs font-medium flex items-center"
                          title="Banear"
                        >
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                          </svg>
                          Banear
                        </button>
                        <button
                          onClick={() => setDeleteModal({ show: true, photo })}
                          className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs font-medium flex items-center"
                          title="Eliminar"
                        >
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Fotos/Videos baneados */}
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <span className="w-3 h-3 bg-red-500 rounded-full mr-2"></span>
              Baneados ({bannedPhotos.length})
            </h3>
            
            {bannedPhotos.length === 0 ? (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                <div className="text-4xl mb-2">🚫</div>
                <p>No hay fotos ni videos baneados</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {bannedPhotos.map((photo) => (
                  <div key={photo.fileName} className="relative group bg-gray-100 rounded-lg overflow-hidden">
                    <img
                      src={getPreviewUrl(photo)}
                      alt={photo.fileName}
                      className="w-full h-32 object-cover grayscale opacity-60"
                      onError={(e) => {
                        if (photo.mediaType === 'video') {
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23000" width="100" height="100"/><polygon fill="%23fff" points="30,20 80,50 30,80"/></svg>';
                        } else {
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23ddd" width="100" height="100"/><text x="50" y="50" text-anchor="middle" dy=".3em" fill="%23999" font-size="10">N/A</text></svg>';
                        }
                      }}
                    />
                    {/* Indicador de video */}
                    {photo.mediaType === 'video' && (
                      <div className="absolute top-1 left-1 bg-black bg-opacity-70 text-white text-xs px-1.5 py-0.5 rounded flex items-center">
                        <svg className="w-2.5 h-2.5 mr-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                        </svg>
                        Video
                      </div>
                    )}
                    <div className="p-2 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                      <div className="flex items-center">
                        <span className="text-red-600 text-xs px-1.5 py-0.5 bg-red-100 rounded font-medium">BANEADO</span>
                      </div>
                      <button
                        onClick={() => handleUnbanPhoto(photo.fileName)}
                        className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs font-medium flex items-center"
                        title="Desbanear"
                      >
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Desbanear
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          </>
      )}

      {deleteModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md">
            <h3 className="text-lg font-bold mb-4">¿Eliminar foto?</h3>
            <p className="text-gray-600 mb-4">Esta acción no se puede deshacer.</p>
            <div className="flex justify-end gap-3">
              <button 
                onClick={() => setDeleteModal({ show: false, photo: null })} 
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancelar
              </button>
              <button 
                onClick={() => {
                  if (deleteModal.photo && handleDeletePhotoPermanently) {
                    handleDeletePhotoPermanently(deleteModal.photo.fileName);
                  }
                  setDeleteModal({ show: false, photo: null });
                }} 
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotosTab;
